import { z } from 'zod';

// v1 onboarding by conversation in the app (mapa de producto «Dar de alta una LÍA»): the Estateflow team creates and
// instructs a project by chatting with LÍA inside the app (same tools as the MCP server, bound to the signed-in user;
// confirms before each change). The project side panel shows the inherited standard vs the project's own decisions and
// the pending items with their owner and closing condition.

const Id = z.string().min(1).max(64);
const Iso = z.string();

export const OnboardingChatMessageSchema = z.object({
  role: z.enum(['user', 'assistant']),
  text: z.string(),
  tools: z.array(z.object({ name: z.string(), ok: z.boolean(), summary: z.string() })).default([]),
  at: Iso,
});
export type OnboardingChatMessage = z.infer<typeof OnboardingChatMessageSchema>;

export const OnboardingChatSchema = z.object({
  id: Id,
  title: z.string(),
  workspaceId: Id.nullable(),
  projectId: Id.nullable(),
  messages: z.array(OnboardingChatMessageSchema),
  /** False when no AI model is configured: the chat says so instead of answering. */
  aiAvailable: z.boolean(),
  createdAt: Iso,
  updatedAt: Iso,
});
export type OnboardingChat = z.infer<typeof OnboardingChatSchema>;

export const CreateOnboardingChatSchema = z.object({
  title: z.string().trim().min(2).max(120).optional(),
  workspaceId: Id.optional(),
  projectId: Id.optional(),
});
export const SendOnboardingMessageSchema = z.object({ text: z.string().trim().min(1).max(4000) });

export const ProjectPendingItemSchema = z.object({
  id: Id,
  projectId: Id,
  title: z.string(),
  ownerLabel: z.string(),
  ownerMembershipId: Id.nullable(),
  closingCondition: z.string(),
  status: z.enum(['open', 'closed']),
  closedNote: z.string().nullable(),
  closedAt: Iso.nullable(),
  source: z.enum(['manual', 'onboarding_chat']),
  createdAt: Iso,
});
export type ProjectPendingItem = z.infer<typeof ProjectPendingItemSchema>;
export const CreateProjectPendingItemSchema = z.object({
  title: z.string().trim().min(3).max(300),
  ownerLabel: z.string().trim().min(2).max(120),
  ownerMembershipId: Id.optional(),
  closingCondition: z.string().trim().min(3).max(500),
});
export const CloseProjectPendingItemSchema = z.object({ note: z.string().trim().min(3).max(1000) });

/** Ficha side panel: inherited standard (read-only layers) vs the project's decisions, plus what is pending. */
export const ProjectSetupOverviewSchema = z.object({
  layers: z.array(z.object({ key: z.string(), title: z.string(), inherited: z.boolean(), version: z.string().nullable(), preview: z.string() })),
  conflicts: z.array(z.object({ layer: z.string(), message: z.string() })),
  pendingItems: z.array(ProjectPendingItemSchema),
  openQuestions: z.number().int(),
  openDiscrepancies: z.number().int(),
});
export type ProjectSetupOverview = z.infer<typeof ProjectSetupOverviewSchema>;
